// ─────────────────────────────────────────────────────────────────────────────
// BizPlus parity — permission administration data hooks (Phase 0.2, wiring)
// ─────────────────────────────────────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface CatalogAction {
  action: string;
  key: string;
  label: string;
}

export interface CatalogModule {
  module: string;
  label: string;
  actions: CatalogAction[];
}

export interface PermissionCatalog {
  modules: CatalogModule[];
  roles: string[];
  totalKeys: number;
}

export interface RolePermissions {
  role: string;
  /** Keys this role resolves to right now (defaults + stored overrides). */
  effective: string[];
  /** Keys the catalogue would give it with no overrides at all. */
  defaults: string[];
  overrides: { key: string; allowed: boolean }[];
  /** True for the owner role, which always holds everything. */
  locked: boolean;
}

export interface UserPermissions {
  userId: string;
  displayName: string;
  role: string;
  effective: string[];
  roleEffective: string[];
  overrides: { key: string; allowed: boolean }[];
  locked: boolean;
}

export function usePermissionCatalog() {
  return useQuery({
    queryKey: ['permissions', 'catalog'],
    // The catalogue is compiled into the build — it cannot change while the
    // page is open, so never refetch it.
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await api.get<PermissionCatalog>('/api/permissions/catalog');
      return data;
    },
  });
}

export function useRolePermissions(role: string | null) {
  return useQuery({
    queryKey: ['permissions', 'role', role],
    enabled: Boolean(role),
    queryFn: async () => {
      const { data } = await api.get<RolePermissions>(`/api/permissions/roles/${role}`);
      return data;
    },
  });
}

export function useSaveRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ role, keys }: { role: string; keys: string[] }) => {
      const { data } = await api.put(`/api/permissions/roles/${role}`, { keys });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['permissions', 'role', vars.role] });
      // The signed-in user's own effective keys may have just changed.
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useUserPermissions(userId: string | null) {
  return useQuery({
    queryKey: ['permissions', 'user', userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data } = await api.get<UserPermissions>(`/api/permissions/users/${userId}`);
      return data;
    },
  });
}

export function useSaveUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, keys }: { userId: string; keys: string[] }) => {
      const { data } = await api.put(`/api/permissions/users/${userId}`, { keys });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['permissions', 'user', vars.userId] });
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}
